import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Theme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import { MediaLibraryImageSelector } from "components/MediaLibraryImageSelector";
import { pick } from "lodash";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { ResourceRequestV2, ResourceV2 } from "services/KnowfullyAdminClient";
import defaultResourcePicture from "../../../public/images/default-picture.jpg";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import { Mode } from "types/common-types";
import { Organizations } from "@knowfully/ui.common";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  lineContainer: {
    display: "flex",
    alignItems: "center",
  },
});

type ResourceFormValues = {
  name?: string;
  prefix?: string;
  bio?: string;
  summary?: string;
  mediaLibraryItemId?: string;
  crmEmail?: string;
  organizationId?: string;
};

const { Form } = withTypes<ResourceFormValues>();

type ResourceFormProps = {
  resource?: ResourceV2;
  submitLabel: string;
  onSubmit: (values: ResourceRequestV2) => void;
  onCancel?: () => void;
  mode: Mode;
};

const ResourceForm = ({
  resource,
  submitLabel,
  onCancel,
  onSubmit,
  mode,
}: ResourceFormProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const validateForm = (values: ResourceFormValues) => {
    const errors: Partial<ResourceFormValues> = {};
    if (
      mode === Mode.Create &&
      values.organizationId === Organizations.KnowfullyLearningGroup
    ) {
      errors.organizationId = t({
        id: "rootOrganizationIsNotAllowedFacultyError",
      });
    }
    return errors;
  };

  const init = useMemo<ResourceFormValues>(
    () => ({
      ...pick(resource ?? {}, [
        "id",
        "name",
        "prefix",
        "summary",
        "bio",
        "mediaLibraryItemId",
        "crmEmail",
        "organizationId",
      ]),
    }),
    [resource]
  );

  const resourceSubmit = (values: ResourceFormValues) => {
    const mediaLibraryItemId =
      values?.mediaLibraryItemId === undefined
        ? null
        : values.mediaLibraryItemId;

    return onSubmit(
      ResourceRequestV2.fromJS({
        value: values.name,
        mediaLibraryItemId: mediaLibraryItemId,
        ...values,
      })
    );
  };

  return (
    <Form
      onSubmit={resourceSubmit}
      initialValues={init}
      validate={(values) => validateForm(values)}
      render={({ handleSubmit, submitting, submitError, values, form }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <ContextOrganizationField
              values={values}
              formChange={form.change}
              mode={
                resource
                  ? ContextOrganizationFieldMode.EditDisabled
                  : ContextOrganizationFieldMode.SetByHeader
              }
            />
            <Grid container spacing={2}>
              <Field name="mediaLibraryItemId">
                {({ input }) => (
                  <MediaLibraryImageSelector
                    {...input}
                    defaultPictureUrl={defaultResourcePicture}
                  />
                )}
              </Field>

              <Field name="prefix">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "prefixLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "nameLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="bio">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FieldRichText
                      input={input}
                      meta={meta}
                      label={t({ id: "bioLabel" })}
                      init={{
                        height: 200,
                      }}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="summary">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      multiline
                      rows={3}
                      label={t({ id: "summaryLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="crmEmail" isEmail>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "crmEmailLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                      InputProps={{
                        readOnly: mode === Mode.Update,
                      }}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={submitting && <CircularProgress size={16} />}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default ResourceForm;
